import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { formatRelative } from "@/lib/utils";
import { formatApiError } from "@/lib/format-api-error";
import { useAddInternalMdfComment } from "./use-add-internal-mdf-comment";
import { useMdfRequestComments } from "./use-mdf-request-comments";
import type { MdfRequestComment } from "./types";

// Sub-bucket 15 — admin-side comments thread on a single request.
//
// Second comment-thread surface in the SPA (first was bucket-3 portal deals;
// bucket 9 partner-side MDF comments share the same Pattern A optimism via
// `use-add-mdf-comment.ts`). Renders both partner + internal authors —
// partner rows are subtly distinct in styling so an admin can scan who's
// who. Pattern A append: optimistic add via `useAddInternalMdfComment` with
// rollback on error; the GET endpoint returns both author_roles in
// chronological order.

export function InternalCommentsThread({ requestId }: { requestId: string }) {
  const comments = useMdfRequestComments(requestId);
  const add = useAddInternalMdfComment();
  const [body, setBody] = useState("");

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;
    add.mutate(
      { requestId, body: trimmed },
      {
        onSuccess: () => setBody(""),
        onError: (err) =>
          toast.error(formatApiError(err, "Failed to post comment")),
      },
    );
  }

  return (
    <section className="space-y-3">
      <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Comments
      </h3>

      {comments.isLoading ? (
        <p className="text-xs text-muted-foreground">Loading comments…</p>
      ) : (comments.data ?? []).length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No comments yet. Notes left here are visible to the partner.
        </p>
      ) : (
        <ul className="space-y-3">
          {(comments.data ?? []).map((c) => (
            <CommentRow key={c.id} comment={c} />
          ))}
        </ul>
      )}

      <form onSubmit={onSubmit} className="space-y-2">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Leave a note for the partner…"
          rows={3}
          maxLength={2000}
          disabled={add.isPending}
        />
        <div className="flex justify-end">
          <Button
            type="submit"
            size="sm"
            disabled={add.isPending || body.trim().length === 0}
          >
            {add.isPending ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="mr-1.5 h-3.5 w-3.5" />
            )}
            Post comment
          </Button>
        </div>
      </form>
    </section>
  );
}

function CommentRow({ comment }: { comment: MdfRequestComment }) {
  const isInternal = comment.author_role === "internal";
  const isPending = comment.id.startsWith("temp-");
  return (
    <li
      className={`rounded-md border px-3 py-2.5 text-sm ${
        isInternal ? "border-accent/30 bg-accent/5" : "border-border/70 bg-card"
      } ${isPending ? "opacity-60" : ""}`}
    >
      <div className="mb-1 flex items-center justify-between gap-3">
        <span
          className={`text-[11px] font-medium uppercase tracking-wider ${
            isInternal ? "text-accent" : "text-muted-foreground"
          }`}
        >
          {isInternal ? "Internal" : "Partner"}
        </span>
        <span className="text-[11px] text-muted-foreground">
          {isPending ? "Sending…" : formatRelative(comment.created_at)}
        </span>
      </div>
      <p className="whitespace-pre-wrap text-sm">{comment.body}</p>
    </li>
  );
}
